import React, {useEffect, useMemo, useRef, useState} from "react";
import {LayersControl, MapContainer, Marker, Popup, TileLayer, useMap} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import ToggleSection, {useToggleSection} from "../../../widget/ToggleSection";
import ToggleTagGroupPanel from "./ToggleTagGroupPanel";
import {
  excludeSelectedSiteDirectionsActive as excludeSelectedSiteDirections,
  includeSelectedSiteDirectionsActive as includeSelectedSiteDirections,
  setExcludedSiteDirectionsActive as setExcludedSiteDirections,
  setIncludedSiteDirectionsActive as setIncludedSiteDirections,
  toggleExcludedSiteDirectionActive as toggleExcludedSiteDirection,
} from "../../../../state/facade/dataFacade";
import {createSvgMarkerIcon} from "../map/createMarkerIcon";
import {ChartColors} from "../constants/chartColors";
import {selectDataMode} from "../../../../state/slice/dataModeSlice";
import {formatSLONumber, formatSLOPercent} from "../utils/FormatNumbers";

const { BaseLayer } = LayersControl;

// UTC, not the viewer's browser zone - minDate/maxDate are backend-bucketed calendar dates.
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, {timeZone: "UTC"}) : "—");

const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || markers.length === 0) return;
    const coords = markers.map((m) => [m.lat, m.lon]);
    const bounds = L.latLngBounds(coords);
    setTimeout(() => {
      try { map.invalidateSize(); } catch {}
      try {
        if (coords.length === 1) map.setView(coords[0], 12);
        else map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      } catch {}
    }, 60);
  }, [map, markers]);
  return null;
};

const GeoLocations = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isHidden, togglerKey } = useToggleSection(["footer"], []);
  const [mapInstance, setMapInstance] = useState(null);
  const mode = useSelector(selectDataMode);
  const excludedSD = useSelector((s) =>
      (mode === "processed"
          ? s.processedMapFiltersSlice.excluded.siteDirections
          : s.mapFiltersSlice.excluded.siteDirections) || []
  );
  const rows = useSelector((s) =>
      (mode === "processed"
          ? s.processedSiteDataAPI.metadata
          : s.siteDataAPI.metadata) || []
  );
  const excludedSDSet = useMemo(() => new Set(excludedSD), [excludedSD]);
  const iconCacheRef = useRef(new Map());

  // normalize rows
  const meta = useMemo(() => {
    return rows.map((r) => ({
      locationId: String(r.locationId || ""),
      siteDirectionName: String(r.siteDirectionName || "").trim(),
      direction: String(r.direction || "Unknown"),
      locationName: r.locationName || r.locationId || "",
      lat: r.latitude ?? r.lat,
      lon: r.longitude ?? r.lon,
      firstMeasurementDate: r.firstMeasurementDate,
      lastMeasurementDate: r.lastMeasurementDate,
      sumAll: Number(r.sumNumAllVehicles) || 0,
      sumNotOver: Number(r.sumNumNotOverloadedVehicles) || 0,
      sumOver: Number(r.sumNumAllOverloadedVehicles) || 0,
    }));
  }, [rows]);

// one marker per site (only if some coords exist)
// group rows by locationId (each row is already aggregated per direction)
  const bySite = useMemo(() => {
    const m = new Map();
    meta.forEach((r) => {
      const arr = m.get(r.locationId) || [];
      arr.push(r);
      m.set(r.locationId, arr);
    });
    return m;
  }, [meta]);

  const siteMarkers = useMemo(() => {
    const out = [];
    for (const [locationId, list] of bySite.entries()) {
      // total up directions directly (each list element = 1 direction summary)
      let minDate = null, maxDate = null, totalAll = 0, totalNotOver = 0, totalOver = 0;

      for (const r of list) {
        if (excludedSDSet.has(r.siteDirectionName)) continue; // skip filtered-out
        if (!minDate || (r.firstMeasurementDate && r.firstMeasurementDate < minDate)) minDate = r.firstMeasurementDate;
        if (!maxDate || (r.lastMeasurementDate && r.lastMeasurementDate > maxDate)) maxDate = r.lastMeasurementDate;
        totalAll     += r.sumAll;
        totalNotOver += r.sumNotOver;
        totalOver    += r.sumOver;
      }

      const anyWithCoords = list.find((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon));
      out.push({
        locationId,
        locationName: anyWithCoords?.locationName || locationId,
        lat: anyWithCoords?.lat ?? null,
        lon: anyWithCoords?.lon ?? null,
        stats: {
          minDate,
          maxDate,
          totals: { all: totalAll, notOver: totalNotOver, over: totalOver },
        },
        siteDirectionNames: list.map(r => r.siteDirectionName),
      });
    }
    return out;
  }, [bySite, excludedSDSet]);


// ✅ all siteDirection filter items, mark disabled if no coords
  const allSiteDirectionItems = useMemo(() => {
    const map = new Map();
    meta.forEach((r) => {
      if (!r.siteDirectionName) return;
      const prev = map.get(r.siteDirectionName) || {
        name: r.siteDirectionName,
        hasCoordinates: false,
      };
      if (Number.isFinite(r.lat) && Number.isFinite(r.lon)) {
        prev.hasCoordinates = true;
      }
      map.set(r.siteDirectionName, prev);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [meta]);

  const excludedSiteDirectionItems = useMemo(
      () => excludedSD.map((name) => ({ name })),
      [excludedSD]
  );

  useEffect(() => {
    if (!mapInstance) return;
    const t = setTimeout(() => {
      try {
        mapInstance.invalidateSize();
      } catch {}
    }, 80);
    return () => clearTimeout(t);
  }, [mapInstance, isHidden]);

  const getCachedIcon = (colorHex) => {
    const key = String(colorHex || '#2B66B5');
    const cached = iconCacheRef.current.get(key);
    if (cached) return cached;
    const ic = createSvgMarkerIcon(key);
    iconCacheRef.current.set(key, ic);
    return ic;
  };

  const inactive = ChartColors?.GRAY || "#9aa0a6";
  const active   = '#2B66B5';
  const fallbackCenter = [46.0511, 14.5051];
  const activeMarkersWithCoords = useMemo(() => siteMarkers.filter(it => it.lat && it.lon), [siteMarkers]);
  const mapCenter = (activeMarkersWithCoords[0] || activeMarkersWithCoords[0])
      ? [(activeMarkersWithCoords[0] || activeMarkersWithCoords[0]).lat,
        (activeMarkersWithCoords[0] || activeMarkersWithCoords[0]).lon]
      : fallbackCenter;

  return (
      <>
        <MapContainer
            // ❌ no default Slovenia fallback

            center={mapCenter}
            zoom={8}
            maxZoom={16}
            ref={(map) => {
              if (map) setMapInstance(map);
            }}
            scrollWheelZoom={false}
            style={{height: '640px', width: '100%', zIndex: 0}}
        >
          <LayersControl position="topright">
            <BaseLayer checked name="Default (OSM)">
              <TileLayer
                  attribution="© OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </BaseLayer>
            <BaseLayer name="Carto Voyager">
              <TileLayer
                  attribution="© OpenStreetMap contributors, © Carto"
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              />
            </BaseLayer>
          </LayersControl>

          <FitBounds markers={activeMarkersWithCoords} />


          {
            activeMarkersWithCoords
          .filter((it) => Number.isFinite(it.lat) && Number.isFinite(it.lon))
          .map((m) => {
            const allExcluded = m.siteDirectionNames.every((sdn) => excludedSDSet.has(sdn));
            const someExcluded = !allExcluded && m.siteDirectionNames.some((sdn) => excludedSDSet.has(sdn));
            const colorHex = allExcluded ? inactive : active;
            const icon = getCachedIcon(colorHex);

            const totalAll  = m?.stats?.totals?.all     ?? 0;
            const totalOver = m?.stats?.totals?.over    ?? 0;
            const totalOK   = m?.stats?.totals?.notOver ?? 0;
            const pctOver = totalAll > 0 ? totalOver / totalAll : 0;
            const pctOK   = totalAll > 0 ? totalOK   / totalAll : 0;
            const siteDirections = new Set(m?.siteDirectionNames);
            const dirCount = siteDirections?.size || 0;

            const fmtInt = (val) => formatSLONumber(val, 0)  || "-"
            const fmtPct = (val) => formatSLOPercent(val) || "0"

            return (
                <Marker
                    key={m.locationId}
                    position={[m.lat, m.lon]}
                    icon={icon}
                    opacity={1}
                    zIndexOffset={1000}
                    eventHandlers={{
                      click: (e) => {
                        const isAccel =
                            !!e?.originalEvent?.ctrlKey || !!e?.originalEvent?.metaKey; // Ctrl on Win/Linux, ⌘ on macOS

                        if (!isAccel) return; // normal click → let Popup open

                        // Ctrl/Cmd + click → toggle include/exclude
                        e.originalEvent.preventDefault?.();
                        e.originalEvent.stopPropagation?.();
                        try { e.target?.closePopup?.(); } catch {}

                        if (allExcluded) {
                          dispatch(includeSelectedSiteDirections(m.siteDirectionNames));
                        } else {
                          dispatch(excludeSelectedSiteDirections(m.siteDirectionNames));
                        }
                      },
                    }}
                >
                  <Popup autoPan={false} keepInView={false} direction="top"   offset={[0, -6]}>
                    {/* Bulma-only content wrapper */}
                    <div className="box p-3">
                      {/* Header */}
                      <div className="mb-2">
                        <div className="is-size-6 has-text-weight-bold">
                          {m.locationName || m.locationId}
                        </div>
                        {m.locationName && m.locationName !== m.locationId && (
                            <div className="is-size-7 has-text-grey">
                              {m.locationId}
                            </div>
                        )}
                      </div>

                      {/* Period */}
                      <div className="level is-mobile mb-2">
                        <div className="level-left">
                          <div className="level-item is-size-7 has-text-grey">
                            {t("map.tooltip.period")}
                          </div>
                        </div>
                        <div className="level-right">
                          <div className="level-item is-size-7 has-text-weight-semibold">
                            {fmtDate(m.stats?.minDate)} – {fmtDate(m.stats?.maxDate)}
                          </div>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="columns is-mobile is-gapless">
                        <div className="column pr-2">
                          <div className="has-text-weight-bold">{fmtInt(totalAll)}</div>
                          <div className="is-size-7 has-text-grey">{t("map.tooltip.totalVehicles")}</div>
                        </div>
                        <div className="column px-2">
                          <div className="has-text-weight-bold">{fmtInt(totalOver)}</div>
                          <div className="is-size-7 has-text-grey">{t("map.tooltip.overloaded")}</div>
                          <div className="tags mt-1">
                            <span className="tag is-danger">{fmtPct(pctOver)}</span>
                          </div>
                        </div>
                        <div className="column pl-2">
                          <div className="has-text-weight-bold">{fmtInt(totalOK)}</div>
                          <div className="is-size-7 has-text-grey">{t("map.tooltip.legallyLoaded")}</div>
                          <div className="tags mt-1">
                            <span className="tag is-success">{fmtPct(pctOK)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Directions + status */}
                      <div className="level is-mobile mt-2">
                        <div className="level-left">
                          <div className="level-item is-size-7 has-text-grey">
                            {t("map.tooltip.directionsCovered")}
                          </div>
                        </div>
                        <div className="level-right">
                          <div className="level-item">
                            <div className="tags has-addons">
                    <span className="tag is-link is-light">
                      {t("map.tooltip.directionsCount", { count: dirCount })}
                    </span>
                              {allExcluded && (
                                  <span className="tag is-danger">{t("map.tooltip.status.allExcluded")}</span>
                              )}
                              {someExcluded && !allExcluded && (
                                  <span className="tag is-warning">{t("map.tooltip.status.partiallyExcluded")}</span>
                              )}
                              {!someExcluded && !allExcluded && (
                                  <span className="tag is-success">{t("map.tooltip.status.included")}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>


                     <div className="notification is-light is-size-7 py-2 px-3 mb-0">
                        {allExcluded
                            ? t("map.tooltip.action.accelClickToInclude", { key: t("map.tooltip.actionKey") })
                            : t("map.tooltip.action.accelClickToExclude", { key: t("map.tooltip.actionKey") })}
                      </div>
                    </div>
                  </Popup>
                </Marker>
            );
          })}
        </MapContainer>

        <ToggleSection
            sectionKey="footer"
            isHidden={isHidden}
            onToggle={togglerKey}
            title={t("dashboards.tabs.PKI3_content.filter")}
        >
          <ToggleTagGroupPanel
              label={t("dashboards_view.kpi3.site_direction_selection")}
              allItems={allSiteDirectionItems}
              excludedItems={excludedSiteDirectionItems}
              id="name"
              labelKeys={["name"]}
              tooltipFormatter={(item) =>
                  !item.hasCoordinates ? t("Coordinates not available") : ""
              }
              onToggleItem={(item) =>
                  dispatch(toggleExcludedSiteDirection(item.name))
              }
              onIncludeAllSelectedFromExclude={(items) =>
                  dispatch(includeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onExcludeAllSelectedFromInclude={(items) =>
                  dispatch(excludeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onFlipSelection={() => {
                const all = allSiteDirectionItems.map((i) => i.name);
                const excluded = new Set(excludedSD);
                const toInclude = all.filter((n) => excluded.has(n));
                const toExclude = all.filter((n) => !excluded.has(n));
                dispatch(setIncludedSiteDirections(toExclude));
                dispatch(setExcludedSiteDirections(toInclude));
              }}
          />
        </ToggleSection>
      </>

  );
};

export default GeoLocations;
